'use client';

import { BannerTileComponent } from '@/components/banner/banner-tile';
import { CinemarkLink } from '@/components/cinemark-link/cinemark-link';
import { LoadingSpinnerComponent } from '@/components/loading-spinner/loading-spinner';
import { MoviePosterComponent } from '@/components/movie-poster/movie-poster';
import { ShowtimeListComponent } from '@/components/showtimes/showtime-list/showtime-list';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import { WatchListIconComponent } from '@/components/watch-list-icon/watch-list-icon';
import { useMovieIdsByTheater } from '@/hooks/movies/movie-ids-by-theater';
import { useMoviesByIds } from '@/hooks/movies/movies';
import { useMoviesHeroScroller } from '@/hooks/movies/movies-hero-scroller';
import { useTheaterShowtimes } from '@/hooks/showtimes/theater-showtimes';
import { useSharedState } from '@/hooks/state/shared-state';
import { useTheaterContext, useUpdateTheaterContext } from '@/hooks/state/theater';
import { useTheaterByTheaterId } from '@/hooks/theater/theater';
import { cn } from '@/lib/utils';
import { MoviePosterShowtime } from '@/types/movie-poster-showtime';
import { MoviesHeroScrollerTile } from '@/types/movies-hero-scroller';
import { TheaterShowtimeItem } from '@/types/showtimes';
import { dataTestTransformer } from '@/utils/data-test-helper';
import { getTodayInTimeZone } from '@/utils/date-time-helper';
import { buildPresaleMessage, presaleDateTimeAppenderValues } from '@/utils/presale-helpers';
import { filterDisplayableShowtimes } from '@/utils/showtime-filters';
import { buildMovieUrlWithDate } from '@/utils/url-params';
import { Theater } from '@cinemark/content-api-client';
import { isSameDay } from 'date-fns';
import { useEffect, useMemo, useRef, useState } from 'react';
import styles from './movie-showtimes-scroller.module.scss';

const DATA_LINK_CATEGORY = 'Movies';
const MAX_SHOWTIMES_PER_MOVIE = 4;
const PLACEHOLDER_MOVIE_POSTER_URL = '/img/poster_blank.jpg';

type MoviesHeroScrollerCarouselItem = {
  campaignTile?: MoviesHeroScrollerTile;
  id: string;
  movieShowtime?: MoviePosterShowtime;
};

const getUsableImageUrl = (...imageUrls: (string | undefined)[]): string | undefined => {
  return imageUrls.find(imageUrl => !!imageUrl && imageUrl !== PLACEHOLDER_MOVIE_POSTER_URL);
};

const buildCarouselItems = (
  showtimes: TheaterShowtimeItem[],
  campaignTiles: MoviesHeroScrollerTile[],
  promoImageUrlsByMovieId: Map<number, string>
): MoviesHeroScrollerCarouselItem[] => {
  const showtimesByMovieId = new Map(showtimes.map(item => [item.cinemarkMovieId, item]));

  const campaignItems = campaignTiles
    .map((campaignTile): MoviesHeroScrollerCarouselItem => {
      if (!campaignTile.movie?.cinemarkMovieId) {
        return { campaignTile, id: campaignTile.id };
      }

      const item = campaignTile.movie?.cinemarkMovieId
        ? showtimesByMovieId.get(campaignTile.movie.cinemarkMovieId)
        : undefined;

      const movieShowtimes = filterDisplayableShowtimes(item?.showtimesByPrintType.flatMap(group => group.showtimes) || [])
        .sort((left, right) => left.startTime.getTime() - right.startTime.getTime())
        .slice(0, MAX_SHOWTIMES_PER_MOVIE);
      const firstShowtime = movieShowtimes[0];
      const publicOnSaleDate = item?.presaleDateTimeAppender === presaleDateTimeAppenderValues.displayDate
        || item?.presaleDateTimeAppender === presaleDateTimeAppenderValues.displayDateAndTime
        ? item.publicOnSaleDate
        : undefined;
      const movie = campaignTile.movie;

      return {
        campaignTile,
        id: campaignTile.id,
        movieShowtime: {
          hasActiveShowtimes: !!firstShowtime,
          hasPresaleShowtimes: item?.hasPresaleShowtimes,
          moviePoster: {
            cinemarkMovieId: item?.cinemarkMovieId || movie?.cinemarkMovieId || 0,
            movieId: item?.movieId || movie?.movieId || 0,
            moviePosterAlt: item?.moviePosterAlt || `${item?.movieTitle || movie?.movieTitle || 'Movie'} poster`,
            moviePosterUrl: getUsableImageUrl(
              campaignTile.bannerImageLarge?.url,
              campaignTile.bannerImageMedium?.url,
              campaignTile.bannerImageSmall?.url,
              movie?.promoLargeImage?.url,
              movie?.promoSmallImage?.url,
              item?.moviePosterUrl
            ),
            movieRating: item?.movieRating,
            movieReleaseDate: item?.movieReleaseDate,
            movieRuntime: item?.movieRuntime,
            movieTitle: item?.movieTitle || movie?.movieTitle,
            movieTitleAttributeList: item?.movieTitleAttributeList,
            movieTrailerUrl: item?.movieTrailerUrl,
            movieUrl: item?.movieUrl || `/movies/${movie?.slug || ''}`,
            publicOnSaleDate,
            theaterId: item?.theaterId || 0,
            theaterName: '',
          },
          presaleLinkText: item?.presaleLinkText,
          presaleLinkUrl: item?.presaleLinkUrl,
          presaleMessage: item?.hasPresaleShowtimes ? buildPresaleMessage(item.movieTitle ?? '', {
            presaleDateTimeAppender: item.presaleDateTimeAppender,
            presaleLinkText: item.presaleLinkText,
            presaleLinkUrl: item.presaleLinkUrl,
            publicOnSaleDate: item.publicOnSaleDate,
          }) : undefined,
          publicOnSaleDate,
          showtime: firstShowtime,
          showtimes: movieShowtimes.length ? movieShowtimes : undefined,
          theaterId: item?.theaterId || 0,
        },
      };
    });

  const showtimeItems = showtimes.flatMap((item): MoviesHeroScrollerCarouselItem[] => {
    const movieShowtimes = filterDisplayableShowtimes(item.showtimesByPrintType.flatMap(group => group.showtimes))
      .sort((left, right) => left.startTime.getTime() - right.startTime.getTime())
      .slice(0, MAX_SHOWTIMES_PER_MOVIE);
    if (!movieShowtimes.length) {
      return [];
    }

    const firstShowtime = movieShowtimes[0];
    const publicOnSaleDate = item.presaleDateTimeAppender === presaleDateTimeAppenderValues.displayDate
      || item.presaleDateTimeAppender === presaleDateTimeAppenderValues.displayDateAndTime
      ? item.publicOnSaleDate
      : undefined;

    return [{
      id: `showtime-${item.cinemarkMovieId}`,
      movieShowtime: {
        hasActiveShowtimes: !!firstShowtime,
        hasPresaleShowtimes: item.hasPresaleShowtimes,
        moviePoster: {
          cinemarkMovieId: item.cinemarkMovieId,
          movieId: item.movieId,
          moviePosterAlt: item.moviePosterAlt || `${item.movieTitle || 'Movie'} poster`,
          moviePosterUrl: getUsableImageUrl(promoImageUrlsByMovieId.get(item.cinemarkMovieId), item.moviePosterUrl),
          movieRating: item.movieRating,
          movieReleaseDate: item.movieReleaseDate,
          movieRuntime: item.movieRuntime,
          movieTitle: item.movieTitle,
          movieTitleAttributeList: item.movieTitleAttributeList,
          movieTrailerUrl: item.movieTrailerUrl,
          movieUrl: item.movieUrl,
          publicOnSaleDate,
          theaterId: item.theaterId,
          theaterName: '',
        },
        presaleLinkText: item.presaleLinkText,
        presaleLinkUrl: item.presaleLinkUrl,
        presaleMessage: item.hasPresaleShowtimes ? buildPresaleMessage(item.movieTitle ?? '', {
          presaleDateTimeAppender: item.presaleDateTimeAppender,
          presaleLinkText: item.presaleLinkText,
          presaleLinkUrl: item.presaleLinkUrl,
          publicOnSaleDate: item.publicOnSaleDate,
        }) : undefined,
        publicOnSaleDate,
        showtime: firstShowtime,
        showtimes: movieShowtimes.length ? movieShowtimes : undefined,
        theaterId: item.theaterId,
      },
    }];
  });

  return [...campaignItems, ...showtimeItems];
};

export const MovieShowtimesScroller = (): React.JSX.Element => {
  const hasRestoredLocation = useRef(false);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(true);
  const [isResolvingInitialLocation, setIsResolvingInitialLocation] = useState(true);
  const [locationError, setLocationError] = useState<string>();
  const [selectedTheater, setSelectedTheater] = useState<Theater>();
  const { data: sharedState, isLoading: isSharedStateLoading } = useSharedState();
  const lastViewedTheaterId = sharedState?.lastViewedTheaterId || 0;
  const { data: theaterContext, isLoading: isTheaterContextLoading } = useTheaterContext();
  const { data: savedTheater, isLoading: isSavedTheaterLoading } = useTheaterByTheaterId(lastViewedTheaterId);
  const { mutateAsync: updateTheaterContext } = useUpdateTheaterContext();

  useEffect(() => {
    const restoreInitialLocation = (): void => {
      if (theaterContext) {
        setLocationError(undefined);
        setSelectedTheater(theaterContext);
        setIsResolvingInitialLocation(false);
        return;
      }

      if (savedTheater) {
        setLocationError(undefined);
        setSelectedTheater(savedTheater);
        void updateTheaterContext(savedTheater);
        setIsResolvingInitialLocation(false);
        return;
      }

      if (hasRestoredLocation.current || isSharedStateLoading || isTheaterContextLoading || isSavedTheaterLoading) {
        return;
      }

      hasRestoredLocation.current = true;

      setIsResolvingInitialLocation(false);
    };

    restoreInitialLocation();
  }, [
    isSavedTheaterLoading,
    isSharedStateLoading,
    isTheaterContextLoading,
    savedTheater,
    sharedState,
    theaterContext,
    updateTheaterContext,
  ]);

  const theaterId = selectedTheater?.theaterId || 0;
  const selectedTheaterName = selectedTheater?.theaterName || selectedTheater?.title || '';
  const theaterTimeZone = selectedTheater?.timeZone;
  const { data: campaignTiles, isError: isCampaignTilesError, isLoading: isCampaignTilesLoading } = useMoviesHeroScroller(theaterId, theaterTimeZone);
  const { data: movieIds, isError: isMovieIdsError, isLoading: isMovieIdsLoading } = useMovieIdsByTheater(theaterId);
  const nowPlayingMovieIds = movieIds?.nowPlayingMovieIds || [];
  const { data: moviesResponse, isError: isMoviesError, isLoading: isMoviesLoading } = useMoviesByIds(nowPlayingMovieIds);
  const resolvedTheaterId = selectedTheater && nowPlayingMovieIds.length > 0 ? theaterId : 0;

  const { data: showtimes, isError: isShowtimesError, isLoading: isShowtimesLoading } = useTheaterShowtimes({
    movieIds: nowPlayingMovieIds,
    theaterId: resolvedTheaterId,
    timeZone: theaterTimeZone,
  });

  const items = useMemo(() => {
    const promoImageUrlsByMovieId = new Map(
      (moviesResponse?.data || [])
        .map(movie => [movie.cinemarkMovieId, getUsableImageUrl(movie.promoLargeImage?.url, movie.promoSmallImage?.url)] as const)
        .filter((entry): entry is [number, string] => !!entry[1])
    );
    const featuredMovieIds = new Set(promoImageUrlsByMovieId.keys());
    const effectiveShowtimes = (showtimes || []).filter(showtime => featuredMovieIds.has(showtime.cinemarkMovieId));

    return buildCarouselItems(effectiveShowtimes, campaignTiles || [], promoImageUrlsByMovieId);
  }, [campaignTiles, moviesResponse, showtimes]);
  const isLoading = isSharedStateLoading || isResolvingInitialLocation || theaterId > 0 &&
    (isCampaignTilesLoading || isMovieIdsLoading || isMoviesLoading) || isShowtimesLoading;
  const hasQueryError = isCampaignTilesError || isMovieIdsError || isMoviesError || isShowtimesError;
  const hasMultipleItems = items.length > 1;
  const carouselClassName = cn('relative', !hasMultipleItems && 'w-full');
  const carouselItemClassName = cn(
    'basis-[calc(100%_-_56px)] first:pl-[6px] last:pr-[6px] mobile-md:basis-[calc(100%_-_40px)] md:basis-[95%]',
    !hasMultipleItems && 'basis-full px-0 mobile-md:basis-full md:basis-full'
  );
  const trendingLocation = selectedTheater?.city || selectedTheaterName || 'Your Area';

  const renderCarouselItem = ({ campaignTile, id, movieShowtime }: MoviesHeroScrollerCarouselItem): React.JSX.Element => {
    if (!selectedTheater || !movieShowtime?.showtimes?.length) {
      return (
        <CarouselItem key={id}
          className={carouselItemClassName}
        >
          <div className={styles['banner-tile']}>
            <BannerTileComponent bannerTile={campaignTile!}
              dataLinkCategory={DATA_LINK_CATEGORY}
            />
          </div>
        </CarouselItem>
      );
    }

    const item = movieShowtime;
    const effectiveShowtimes = item.showtimes || [];
    const moreShowtimesHref = effectiveShowtimes.length
      ? buildMovieUrlWithDate(item.moviePoster.movieUrl, effectiveShowtimes[0].startTime, theaterTimeZone)
      : undefined;
    const showtimeLabel = effectiveShowtimes.length && isSameDay(
      getTodayInTimeZone(theaterTimeZone, effectiveShowtimes[0].startTime),
      getTodayInTimeZone(theaterTimeZone)
    )
      ? "Today's Showtimes at"
      : 'Starting Soon at';

    return (
      <CarouselItem key={id}
        className={carouselItemClassName}
      >
        <article className={cn(
          'flex min-h-full flex-col items-stretch overflow-hidden rounded-[8px] border border-[var(--border)]',
          'bg-[var(--surface)] shadow-[var(--surface-shadow)]',
          'md:grid md:h-[480px] md:min-h-[480px] md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]',
          'md:grid-rows-[auto_1fr] md:items-center'
        )}
        data-test={`movies-showtimes-card-${item.moviePoster.cinemarkMovieId}`}
        >
          <div className={cn(
            'flex items-center justify-between gap-[12px] px-[12px] pt-[12px] pb-[10px]',
            'md:col-start-2 md:row-start-1 md:items-start md:px-[24px] md:pt-ms md:pb-0'
          )}
          >
            <p className="text-[0.9rem] leading-[1.2] font-bold text-[var(--text-primary)]"
              data-test={`movies-showtimes-trending-${item.moviePoster.cinemarkMovieId}`}
            >
              Trending in {trendingLocation}
            </p>

            <WatchListIconComponent className="size-ms fill-none stroke-current text-[var(--primary)] [&_use]:fill-current [&_use]:stroke-current"
              movieId={item.moviePoster.cinemarkMovieId}
              movieTitle={item.moviePoster.movieTitle || ''}
            />
          </div>

          <div className="shrink-0 md:col-start-1 md:row-span-2 md:row-start-1 md:self-stretch">
            <MoviePosterComponent showPlayButton
              dataLinkCategory={DATA_LINK_CATEGORY}
              dataLinkName={`Movie - ${item.moviePoster.movieTitle}`}
              hasActiveShowtimes={item.hasActiveShowtimes}
              hasPresaleShowtimes={item.hasPresaleShowtimes}
              movie={item.moviePoster}
              posterClassName={cn(
                'w-full [&_a]:block [&_a]:h-auto [&_img]:aspect-video [&_img]:h-auto [&_img]:rounded-none',
                'md:h-full md:max-w-none md:[&_a]:h-full md:[&_img]:aspect-auto md:[&_img]:h-full'
              )}
              presaleLinkText={item.presaleLinkText}
              presaleLinkUrl={item.presaleLinkUrl}
              presaleMessage={item.presaleMessage}
              publicOnSaleDate={item.publicOnSaleDate}
              showtimes={effectiveShowtimes}
              showTitle={false}
              showWatchlistButton={false}
              theaterName={selectedTheaterName}
              trailerDataLinkName={`Play Trailer - ${item.moviePoster.movieTitle}`}
            />
          </div>

          <div className={cn(
            'flex flex-1 flex-col justify-between gap-[12px] px-[12px] pt-[10px] pb-[12px]',
            'md:col-start-2 md:row-start-2 md:min-h-full md:gap-[24px] md:px-[24px] md:pt-0 md:pb-ms'
          )}
          >
            <div className="flex items-start">
              <div className="flex flex-col gap-[8px]">
                <CinemarkLink className={cn(
                  'inline-flex text-lg leading-[1.1] font-bold text-[var(--text-primary)] no-underline',
                  'hover:text-[var(--primary)]'
                )}
                dataLinkCategory={DATA_LINK_CATEGORY}
                dataLinkName={`Movie - ${item.moviePoster.movieTitle}`}
                dataTest={item.moviePoster.movieTitle
                  ? `link-movie-showtimes-${dataTestTransformer(item.moviePoster.movieTitle)}`
                  : `link-movie-showtimes-${item.moviePoster.cinemarkMovieId}`}
                href={item.moviePoster.movieUrl}
                >
                  {item.moviePoster.movieTitle}
                </CinemarkLink>
              </div>
            </div>

            {!!effectiveShowtimes.length &&
              <div className="flex flex-col gap-[16px]">
                <div className="flex flex-col gap-[4px]"
                  data-test={`movies-showtimes-theater-${item.moviePoster.cinemarkMovieId}`}
                >
                  <p className="text-[0.8rem] font-medium tracking-[0.02em] text-[var(--text-secondary)] normal-case"
                    data-test={`movies-showtimes-theater-label-${item.moviePoster.cinemarkMovieId}`}
                  >
                    {showtimeLabel}
                  </p>
                  <p className="text-base leading-[1.3] font-bold text-[var(--text-primary)]"
                    data-test={`movies-showtimes-theater-name-${item.moviePoster.cinemarkMovieId}`}
                  >
                    {selectedTheaterName}
                  </p>
                </div>

                <ShowtimeListComponent forceChipStyle
                  cinemarkMovieId={item.moviePoster.cinemarkMovieId}
                  dataLinkCategory={DATA_LINK_CATEGORY}
                  listClassName="justify-start gap-sm"
                  showtimes={effectiveShowtimes}
                  theaterId={item.theaterId}
                  timeZone={theaterTimeZone}
                />
              </div>
            }

            {moreShowtimesHref &&
              <CinemarkLink className={cn(
                'inline-flex w-full items-center justify-center self-center border-t border-[var(--border)] pt-[16px]',
                'text-[0.9rem] font-bold text-[var(--primary)] no-underline',
                'sm:self-start sm:justify-start md:border-t-0 md:pt-0'
              )}
              dataLinkCategory={DATA_LINK_CATEGORY}
              dataLinkName="More Showtimes"
              dataTest={`link-more-showtimes-${item.moviePoster.cinemarkMovieId}`}
              href={moreShowtimesHref}
              >
                More Showtimes
              </CinemarkLink>
            }
          </div>
        </article>
      </CarouselItem>
    );
  };

  return (
    <section className="mx-auto flex w-full max-w-[1420px] flex-col gap-ms"
      data-test="movies-showtimes-scroller"
    >
      {locationError &&
        <p className="text-[0.95rem] text-[var(--destructive)]"
          data-test="movies-showtimes-scroller-error"
        >
          {locationError}
        </p>
      }

      {isLoading &&
        <div className="flex justify-center py-md"
          data-test="movies-showtimes-scroller-loading"
        >
          <LoadingSpinnerComponent center="within" />
        </div>
      }

      {!isLoading && hasQueryError &&
        <p className="text-[0.95rem] text-[var(--destructive)]"
          data-test="movies-showtimes-scroller-load-error"
        >
          Unable to load showtimes right now. Please try again later.
        </p>
      }

      {!isLoading && !hasQueryError && !!selectedTheater && !campaignTiles?.length && !locationError &&
        <div className="flex flex-col gap-[8px] rounded-[12px] border border-[var(--border)] bg-[var(--surface)] p-[24px]"
          data-test="movies-showtimes-scroller-empty"
        >
          <p className="text-[1.125rem] font-bold"
            data-test="movies-showtimes-scroller-empty-title"
          >
            No Showtimes Found
          </p>
          <p className="text-[0.95rem] leading-[1.5] text-[var(--text-secondary)]"
            data-test="movies-showtimes-scroller-empty-copy"
          >
            No showtimes are available for the nearest theatre right now.
          </p>
        </div>
      }

      {!isLoading && !hasQueryError && !!items.length &&
        <div data-test="movies-showtimes-scroller-carousel">
          <Carousel className={carouselClassName}
            opts={{
              align: 'center',
              loop: false,
            }}
            setApi={(api) => {
              if (api) {
                const updateButtons = (): void => {
                  const progress = api.scrollProgress();
                  setCanScrollPrev(progress > 0.01);
                  setCanScrollNext(progress < 0.99);
                };
                updateButtons();
                api.on('scroll', updateButtons);
                api.on('select', updateButtons);
                api.on('settle', updateButtons);
              }
            }}
          >
            <CarouselContent className="items-stretch gap-sm">
              {items.map(renderCarouselItem)}
            </CarouselContent>

            {hasMultipleItems && canScrollPrev &&
              <CarouselPrevious className="left-[12px] z-[2] hidden md:inline-flex"
                dataTest="button-movies-showtimes-previous"
                iconFontSizeClassName="size-7"
                variant="secondary"
              />
            }
            {hasMultipleItems && canScrollNext &&
              <CarouselNext className="right-[12px] z-[2] hidden md:inline-flex"
                dataTest="button-movies-showtimes-next"
                iconFontSizeClassName="size-7"
                variant="secondary"
              />
            }
          </Carousel>
        </div>
      }
    </section>
  );
};
